import { nextTick } from 'vue'
import type { PlaceholderAllocationAttemptRequest } from '@/Network/Types/Requests/StationRequests'

/**
 * Apoio à integração do aviso "Este médico não pode ser usado como coringa"
 * (`PlaceholderDoctorBlockedModal` + `usePlaceholderDoctorBlocked`) nos modais que alocam
 * médico: bloqueio preventivo no autocomplete, telemetria do bloqueio preventivo e foco no
 * campo "Responsável pela cobertura" depois do "Usar Ocupado sem identificação".
 */

export interface PlaceholderDoctorFlags {
  /** Médicos "coringa" recusados pelo BE nesta escala. Ausente = BE antigo (só o 422 vale). */
  blockedPlaceholderDoctorIds?: number[] | null
}

/** O médico escolhido é um "coringa" bloqueado nesta escala (só com o BE que manda a lista). */
export function isBlockedPlaceholderDoctor(
  schedule: PlaceholderDoctorFlags | null | undefined,
  doctorId: string | number | null | undefined
): boolean {
  const ids = schedule?.blockedPlaceholderDoctorIds
  if (!Array.isArray(ids) || ids.length === 0) return false
  if (doctorId === null || doctorId === undefined || doctorId === '') return false
  const id = Number(doctorId)
  return Number.isFinite(id) && ids.includes(id)
}

/** Nome exibido do médico nas listas de autocomplete (`{ value, title }`), ou `null`. */
export function findDoctorTitle(
  items: ReadonlyArray<{ value: unknown; title?: string | null }> | null | undefined,
  doctorId: string | number | null | undefined
): string | null {
  if (doctorId === null || doctorId === undefined || doctorId === '') return null
  const found = (items ?? []).find((item) => String(item.value) === String(doctorId))
  return found?.title || null
}

type SendAttempt = (payload: PlaceholderAllocationAttemptRequest) => Promise<unknown> | unknown

/**
 * Telemetria do bloqueio PREVENTIVO (antes de chamar o BE). Fire-and-forget: não é aguardada
 * pela UI e qualquer falha (síncrona ou rejeição) vai só para o console — nunca toast, nunca
 * quebra o aviso. No caminho do 422 NÃO chamar: o BE já registra a tentativa.
 */
export function reportPreventivePlaceholderAttempt(
  send: SendAttempt | null | undefined,
  payload: Omit<PlaceholderAllocationAttemptRequest, 'entryPoint'>
): void {
  const body: PlaceholderAllocationAttemptRequest = {
    doctorId: payload.doctorId,
    scheduleId: payload.scheduleId,
    ...(payload.stationIds?.length ? { stationIds: payload.stationIds } : {}),
    entryPoint: 'WEB_PREVENTIVE'
  }
  const warn = (error: unknown) =>
    console.warn('Falha ao registrar a tentativa de alocar médico coringa', error)
  try {
    if (typeof send !== 'function') return
    Promise.resolve(send(body)).catch(warn)
  } catch (error) {
    warn(error)
  }
}

export const COVERAGE_RESPONSIBLE_SELECT_SELECTOR = '[data-testid="coverage-responsible-select"]'

/**
 * Foca o `input` do campo que casa com `selector` (ou o próprio elemento). Espera o render
 * (campos `v-if`) e uma macrotask: o v-dialog do aviso precisa terminar de fechar (senão a
 * trava de foco dele devolve o foco para dentro do diálogo).
 */
export async function focusFieldBySelector(selector: string): Promise<void> {
  await nextTick()
  await new Promise<void>((resolve) => setTimeout(resolve, 0))
  const root = document.querySelector<HTMLElement>(selector)
  if (!root) return
  const target = root.matches('input') ? root : root.querySelector<HTMLElement>('input')
  ;(target ?? root).focus()
}

/** Foca "Responsável pela cobertura" depois que o modo vira UNIDENTIFIED. */
export function focusCoverageResponsibleSelect(): Promise<void> {
  return focusFieldBySelector(COVERAGE_RESPONSIBLE_SELECT_SELECTOR)
}
